import { action, computed, makeObservable, observable, override } from 'mobx';

import { Tab } from '../../app/configurator/Tab';
import { IApiClient } from '../../app/data/client';
import {
  GetDealerFavoritesQuery,
  OrderSortDirection,
  RemoveProductFromDealerFavoritesCommand,
  UpdateFavoriteConfigurationLabelCommand,
} from '../../app/data/model';
import type {
  FavoritesSummaryData,
  ShoppingCartData,
  ShoppingCartItemData,
  ShoppingContext,
} from '../../app/data/model';
import { ConfiguratorUrlBuilder } from '../../app/errorHandling/ParseConfigurationCodeAndRedirectState';
import { BasePageState } from '../../app/shared/BasePageState';
import type { PageMetadata } from '../../app/shared/common';
import { IEventAggregator } from '../../app/shared/EventAggregator';
import { TextInput } from '../../app/shared/Form';
import { INavigationService } from '../../app/shared/NavigationService';
import { ZipBuilder } from '../../app/shared/ZipBuilder';
import { StoreState } from '../../app/StoreState';
import { ICartItemContextAction } from '../cart/CartItem/CartItemState';
import { FavoritesUpdatedEvent } from '../cart/events/FavoritesUpdatedEvent';
import { FavoritesPageTranslation } from '../localization/SiteTranslation';
import { BuyerPolicy } from '../Policy';
import { EditConfigurationNameModalState } from './EditConfigurationNameModalState';
import { FavoriteItemModalState } from './FavoriteItemModalState';
import { FavoriteItemState } from './FavoriteItemState';
import { RemovalConfirmationModalState } from './RemovalConfirmationModalState';

interface ISortOrderItem {
  label: string;
  direction: OrderSortDirection;
  propertyName: string;
  key?: string;
}

export class FavoritesState extends BasePageState<null> {
  @observable.ref
  favoritesCart?: ShoppingCartData;

  @observable.ref
  shoppingContext?: ShoppingContext;

  @observable
  public readonly editConfigurationNameModal: EditConfigurationNameModalState;

  @observable
  public readonly favoriteItemDetailsModal: FavoriteItemModalState;

  @observable
  public readonly removalConfirmationModal: RemovalConfirmationModalState;

  @observable
  public searchInput: TextInput = new TextInput();

  public readonly favoriteItemContextActions: ICartItemContextAction[] = [];

  private readonly configurationUrlBuilder: ConfiguratorUrlBuilder;
  private readonly zipBuilder: ZipBuilder;

  public readonly sortOrderItems: Array<ISortOrderItem>;

  @observable
  public selectedSortOrderItem: ISortOrderItem;

  constructor(
    private readonly client: IApiClient,
    private readonly navigation: INavigationService,
    private readonly eventAggregator: IEventAggregator,
    public readonly translation: FavoritesPageTranslation,
  ) {
    super();
    makeObservable(this);
    this.configurationUrlBuilder = new ConfiguratorUrlBuilder(this.client);
    this.zipBuilder = new ZipBuilder(this.client, this.navigation);

    this.editConfigurationNameModal = new EditConfigurationNameModalState(
      this.updatefavoriteConfigurationLabel,
      this.translation.editConfigurationNameModal,
      this.translation.favoriteItemDetailsModal,
    );
    this.favoriteItemDetailsModal = new FavoriteItemModalState(this.translation.favoriteItemDetailsModal);
    this.removalConfirmationModal = new RemovalConfirmationModalState(
      this.removeProductFromFavorites,
      this.translation.removalConfirmationModal,
      this.translation.favoriteItemDetailsModal,
    );

    this.sortOrderItems = [
      {
        label: translation.sortByDateAscLabel,
        direction: OrderSortDirection.Ascending,
        propertyName: 'createdDate',
        key: 'createdDate' + OrderSortDirection.Ascending,
      },
      {
        label: translation.sortByDateDescLabel,
        direction: OrderSortDirection.Descending,
        propertyName: 'createdDate',
        key: 'createdDate' + OrderSortDirection.Descending,
      },
      {
        label: translation.sortByNameAscLabel,
        direction: OrderSortDirection.Ascending,
        propertyName: 'baseModel.name',
        key: 'baseModel.name' + OrderSortDirection.Ascending,
      },
      {
        label: translation.sortByNameDescLabel,
        direction: OrderSortDirection.Descending,
        propertyName: 'baseModel.name',
        key: 'baseModel.name' + OrderSortDirection.Descending,
      },
      {
        label: translation.sortByPriceAscLabel,
        direction: OrderSortDirection.Ascending,
        propertyName: 'totalPrice.amount',
        key: 'totalPrice.amount' + OrderSortDirection.Ascending,
      },
      {
        label: translation.sortByPriceDescLabel,
        direction: OrderSortDirection.Descending,
        propertyName: 'totalPrice.amount',
        key: 'totalPrice.amount' + OrderSortDirection.Descending,
      },
    ];

    this.setSelectedSortOrderItem(this.sortOrderItems[0]);
  }

  @action.bound
  setSelectedSortOrderItem(item: ISortOrderItem) {
    this.selectedSortOrderItem = item;
  }

  @computed
  get favoriteItems(): FavoriteItemState[] {
    if (!this.favoritesCart) {
      return [];
    }

    let items = this.favoritesCart.items;

    if (!!this.searchInput.value) {
      items = items.filter((item) => this.matchesSearchTerm(item, this.searchInput.value));
    }

    if (this.selectedSortOrderItem) {
      items = items.slice().sort((a, b) => {
        const aValue = this.getNestedPropertyValue(a, this.selectedSortOrderItem.propertyName);
        const bValue = this.getNestedPropertyValue(b, this.selectedSortOrderItem.propertyName);
        if (aValue < bValue) {
          return this.selectedSortOrderItem.direction === OrderSortDirection.Ascending ? -1 : 1;
        }
        if (aValue > bValue) {
          return this.selectedSortOrderItem.direction === OrderSortDirection.Ascending ? 1 : -1;
        }
        return 0;
      });
    }

    return items.map(
      (item) =>
        new FavoriteItemState(
          item,
          {
            addToCart: this.addFavoriteToCart,
            contextActions: this.favoriteItemContextActions,
            showDetails: this.openFavoriteItemDetailsModal,
          },
          this.translation.favoriteItem,
        ),
    );
  }

  private getNestedPropertyValue(shoppingCartItem: ShoppingCartItemData, propertyPath: string) {
    const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

    return propertyPath
      .split('.')
      .reduce((acc, part) => (isObject(acc) ? acc[part] : undefined), shoppingCartItem as unknown);
  }

  @override
  get metadata(): PageMetadata {
    return {
      title: this.translation.title ?? 'My favourites',
    };
  }

  async onLoad(store: StoreState) {
    const response = await this.client.send(
      new GetDealerFavoritesQuery({
        shoppingContext: store.shoppingContext,
      }),
    );

    this.setFavoritesCart(response);
    this.setItemContextActions(store.shoppingContext);
    this.setShoppingContext(store.shoppingContext);
  }

  @action.bound
  public handleOnSelectedSortOrderItemChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const item = this.sortOrderItems.find((x) => x.key === event.target.value);
    this.setSelectedSortOrderItem(item);
  };

  @action.bound
  private setFavoritesCart({
    favoritesCart,
    favoritesSummary,
  }: {
    favoritesCart: ShoppingCartData;
    favoritesSummary: FavoritesSummaryData;
  }) {
    this.favoritesCart = favoritesCart;
    this.eventAggregator.publish(FavoritesUpdatedEvent.fromFavoritesSummary(favoritesSummary));
  }

  private setItemContextActions(shoppingContext: ShoppingContext) {
    this.favoriteItemContextActions.push({
      label: this.translation.downloadImagesLabel,
      action: this.generateZipVisualizationImages,
    });

    if (BuyerPolicy.grantAccess(shoppingContext.customer)) {
      this.favoriteItemContextActions.unshift({
        label: this.translation.editNameLabel,
        action: this.openProductConfigurationFormModal,
      });

      this.favoriteItemContextActions.push({
        label: this.translation.removeLabel,
        action: this.openRemovalConfirmationModal,
      });
    }
  }

  @action.bound
  private setShoppingContext(shoppingContext: ShoppingContext) {
    this.shoppingContext = shoppingContext;
  }

  private matchesSearchTerm(item: ShoppingCartItemData, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase();
    return (
      (item.customName?.toLowerCase().includes(term) ?? false) ||
      (item.baseModel.shortName?.toLowerCase().includes(term) ?? false) ||
      (item.baseModel.brandName?.toLowerCase().includes(term) ?? false) ||
      (item.code?.toLowerCase().includes(term) ?? false)
    );
  }

  private addFavoriteToCart = async (favoriteItem: ShoppingCartItemData) => {
    const shopUrl = await this.configurationUrlBuilder.buildFromCode(favoriteItem.code, undefined, Tab.Summary);
    this.navigation.navigateTo(shopUrl);
  };

  private removeProductFromFavorites = async (favoriteItem: ShoppingCartItemData) => {
    const response = await this.client.send(
      new RemoveProductFromDealerFavoritesCommand({
        lineItemId: favoriteItem.id,
        shoppingContext: this.shoppingContext,
      }),
    );

    this.setFavoritesCart(response);
  };

  private generateZipVisualizationImages = async (item: ShoppingCartItemData) => {
    await this.zipBuilder.downloadZipConfigurationImages(item.code);
  };

  private openProductConfigurationFormModal = async (item: ShoppingCartItemData) => {
    this.editConfigurationNameModal.setFavoriteItem(item);
    this.editConfigurationNameModal.open();
  };

  private openFavoriteItemDetailsModal = async (item: ShoppingCartItemData) => {
    this.favoriteItemDetailsModal.setFavoriteItem(item);
    this.favoriteItemDetailsModal.open();
  };

  private openRemovalConfirmationModal = async (item: ShoppingCartItemData) => {
    this.removalConfirmationModal.setFavoriteItem(item);
    this.removalConfirmationModal.open();
  };

  private updatefavoriteConfigurationLabel = async (favoriteItem: ShoppingCartItemData, newLabel: string) => {
    const response = await this.client.send(
      new UpdateFavoriteConfigurationLabelCommand({
        lineItemId: favoriteItem.id,
        label: newLabel,
        shoppingContext: this.shoppingContext,
      }),
    );

    this.setFavoritesCart(response);
  };
}
